/* akk-quote.js — Tilboðslistinn (the quote list) and the VSK toggle.
   Not the Shopify cart: a buyer assembles a list, and it leaves the store
   through the contact form as text. State lives in localStorage; the elements
   below share it through document-level events, so the rail (one section) and
   the list page stay in step. With JS off the page still renders with án-vsk
   prices (CSS default) and the form still sends. */

(() => {
  'use strict';

  /* Several sections load this file; browsers do not dedupe identical scripts. */
  if (window.__akkQuoteLoaded) return;
  window.__akkQuoteLoaded = true;

  const STORE_KEY = 'akk:tilbodslisti:v1';
  const VSK_KEY = 'akk:vsk:v1';
  const CHANGE = 'akk:quote:change';
  const VSK_CHANGE = 'akk:vsk:change';

  /**
   * @typedef {object} QuoteItem
   * @property {string} id
   * @property {string} title
   * @property {string} url
   * @property {string} image
   * @property {number} price   Inc-VAT unit price in minor units.
   * @property {number} qty
   * @property {boolean} varies "Frá" pricing.
   */

  /* Thousands grouped with a dot, by hand: Intl.NumberFormat('is-IS') falls
     back to commas in browsers shipping trimmed ICU data. Matches the store's
     amount_no_decimals_with_comma_separator format: "10.355 kr." */
  const group = (n) => String(Math.round(n)).replace(/\B(?=(\d{3})+(?!\d))/g, '.');
  const money = (cents) => `${group(cents / 100)} kr.`;

  function readJSON(key, fallback) {
    try {
      const raw = window.localStorage.getItem(key);
      return raw === null ? fallback : JSON.parse(raw);
    } catch {
      return fallback;
    }
  }
  function writeJSON(key, value) {
    try {
      window.localStorage.setItem(key, JSON.stringify(value));
    } catch {
      /* blocked storage: the in-memory list still works for this pageview */
    }
  }

  const isValidItem = (item) =>
    Boolean(item && typeof item.id === 'string' && typeof item.title === 'string' &&
      Number.isFinite(item.price) && Number.isFinite(item.qty) && item.qty > 0);

  const Quote = {
    /** @type {QuoteItem[]} */
    items: [],
    /* The last box taken off the list and where it sat, for "Afturkalla".
       Any other change to the list forgets it. @type {{item: QuoteItem, index: number} | null} */
    lastRemoved: null,
    /* 100 + VSK%; set from data-akk-vat on markup. */
    vatDivisor: 124,
    /* Products on the theme's lower-rate list and that rate, and the print's own
       prices by handle ([ex, inc] in krónur, 0 = not printed), from #akk-vsk-low. */
    lowIds: new Set(),
    lowDivisor: 111,
    print: {},

    load() {
      const stored = readJSON(STORE_KEY, []);
      this.items = Array.isArray(stored) ? stored.filter(isValidItem) : [];
    },
    save() {
      writeJSON(STORE_KEY, this.items);
      document.dispatchEvent(new CustomEvent(CHANGE, { detail: this.totals() }));
    },
    /* Ex-VAT unit price rounded to whole krónur BEFORE multiplying, the same
       sum akk-price-vsk runs in Liquid, so line = printed unit × qty. */
    /* One unit's prices in minor units, the same sums akk-price-vsk runs: a printed
       item takes the print's figures, else the store price with the ex-VAT figure at
       the product's rate. ref is an item or {id, handle}; an item's handle is in its url. */
    unit(cents, ref) {
      const handle = ref?.handle || /\/products\/([^/?#]+)/.exec(ref?.url ?? '')?.[1] || '';
      const [printEx, printInc] = this.print[handle] ?? [0, 0];
      const inc = printInc ? printInc * 100 : cents;
      const divisor = this.lowIds.has(String(ref?.id)) ? this.lowDivisor : this.vatDivisor;
      return { inc, ex: printEx ? printEx * 100 : Math.round(inc / divisor) * 100 };
    },
    ex(cents, ref) {
      return this.unit(cents, ref).ex;
    },
    inc(cents, ref) {
      return this.unit(cents, ref).inc;
    },
    find(id) {
      return this.items.find((item) => item.id === id);
    },
    add(item) {
      this.lastRemoved = null;
      const existing = this.find(item.id);
      if (existing) existing.qty += item.qty;
      else this.items.push(item);
      this.save();
    },
    setQty(id, qty) {
      const item = this.find(id);
      if (!item) return;
      if (qty < 1) { this.remove(id); return; }
      this.lastRemoved = null;
      item.qty = qty;
      this.save();
    },
    remove(id) {
      const index = this.items.findIndex((item) => item.id === id);
      if (index === -1) return;
      this.lastRemoved = { item: this.items[index], index };
      this.items.splice(index, 1);
      this.save();
    },
    /** Puts the last removed box back where it was. @returns {string | null} its id */
    restore() {
      const last = this.lastRemoved;
      if (!last) return null;
      this.lastRemoved = null;
      if (!this.find(last.item.id)) this.items.splice(Math.min(last.index, this.items.length), 0, last.item);
      this.save();
      return last.item.id;
    },
    totals() {
      let inc = 0, exVat = 0, units = 0, varies = false;
      for (const item of this.items) {
        inc += this.inc(item.price, item) * item.qty;
        exVat += this.ex(item.price, item) * item.qty;
        units += item.qty;
        if (item.varies) varies = true;
      }
      return { lines: this.items.length, units, inc, exVat, varies };
    },
  };

  function readVatDivisor() {
    const host = document.querySelector('[data-akk-vat]');
    const rate = Number.parseInt(host?.dataset.akkVat ?? '', 10);
    if (Number.isFinite(rate) && rate >= 0 && rate < 100) Quote.vatDivisor = 100 + rate;
    try {
      const low = JSON.parse(document.getElementById('akk-vsk-low')?.textContent || 'null');
      if (low && Number.isFinite(low.rate) && Array.isArray(low.ids)) {
        Quote.lowDivisor = 100 + low.rate;
        Quote.lowIds = new Set(low.ids.map(String));
      }
      if (low && low.print && typeof low.print === 'object') Quote.print = low.print;
    } catch {
      /* no list: every product keeps the section's rate */
    }
  }

  const mode = () => (document.documentElement.dataset.akkVsk === 'inc' ? 'inc' : 'ex');
  const unitCents = (item) => (mode() === 'inc' ? Quote.inc(item.price, item) : Quote.ex(item.price, item));
  const priceLabel = () => (mode() === 'inc' ? 'Verð með vsk.' : 'Verð án vsk.');
  const items = (n) => (n === 1 ? '1 vara' : `${group(n)} vörur`);
  const gifts = (n) => (n === 1 ? '1 gjöf' : `${group(n)} gjafir`);
  const boxes = (n) => `${group(n)} box`;

  function setVskMode(next) {
    const value = next === 'inc' ? 'inc' : 'ex';
    document.documentElement.dataset.akkVsk = value;
    writeJSON(VSK_KEY, value);
    document.dispatchEvent(new CustomEvent(VSK_CHANGE, { detail: { mode: value } }));
    document.dispatchEvent(new CustomEvent(CHANGE, { detail: Quote.totals() }));
  }

  /* The "„Box“ fjarlægt. Afturkalla" line under a list. The region is a
     role=status paragraph; it is rebuilt only when the removed box changes so
     a focused button is not swapped out from under the keyboard. */
  function renderUndo(region) {
    if (!region) return;
    const last = Quote.lastRemoved;
    if (!last) {
      if (region.firstChild) region.replaceChildren();
      delete region.dataset.forId;
      return;
    }
    if (region.dataset.forId === last.item.id && region.firstChild) return;
    region.dataset.forId = last.item.id;
    const undo = region.dataset.labelUndo || 'Afturkalla';
    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'akk-undo__btn';
    button.dataset.akkUndo = '';
    button.textContent = undo;
    button.setAttribute('aria-label', `${undo}: ${last.item.title}`);
    region.replaceChildren(`${last.item.title} ${region.dataset.labelRemoved || 'fjarlægt.'}`, button);
  }

  /* --- <akk-vsk-toggle>: two buttons; the mode lives on <html> and CSS
     switches the pre-rendered án/með spans, so nothing flashes. */
  class AkkVskToggle extends HTMLElement {
    connectedCallback() {
      this.buttons = [...this.querySelectorAll('[data-vsk]')];
      for (const button of this.buttons) button.addEventListener('click', this.#onClick);
      document.addEventListener(VSK_CHANGE, this.#reflect);
      this.#reflect();
    }
    disconnectedCallback() {
      for (const button of this.buttons ?? []) button.removeEventListener('click', this.#onClick);
      document.removeEventListener(VSK_CHANGE, this.#reflect);
    }
    #onClick = (event) => setVskMode(event.currentTarget.dataset.vsk);
    #reflect = () => {
      const current = mode();
      for (const button of this.buttons ?? []) {
        button.setAttribute('aria-pressed', String(button.dataset.vsk === current));
      }
    };
  }

  /* --- <akk-quote-add>: one press adds the section's quantity (1 unless the
     editor says otherwise; the 10 it used to be read as a suggestion, CEO
     2026-10-13 #19); a box already on the list reads "Í tilboðslista · 1 stk"
     and a press goes to the list. */
  class AkkQuoteAdd extends HTMLElement {
    connectedCallback() {
      this.button = this.querySelector('button');
      this.label = this.querySelector('[data-akk-add-label]');
      if (!this.button) return;
      this.button.addEventListener('click', this.#onClick);
      document.addEventListener(CHANGE, this.#render);
      this.#render();
    }
    disconnectedCallback() {
      this.button?.removeEventListener('click', this.#onClick);
      document.removeEventListener(CHANGE, this.#render);
    }
    #onClick = (event) => {
      event.preventDefault();
      const id = this.dataset.id ?? '';
      if (Quote.find(id)) {
        if (this.dataset.listUrl) window.location.assign(this.dataset.listUrl);
        return;
      }
      Quote.add({
        id,
        title: this.dataset.title ?? '',
        url: this.dataset.url ?? '',
        image: this.dataset.image ?? '',
        price: Number.parseInt(this.dataset.price ?? '0', 10),
        qty: Number.parseInt(this.dataset.qty ?? '1', 10) || 1,
        varies: this.dataset.varies === 'true',
      });
    };
    #render = () => {
      const item = Quote.find(this.dataset.id ?? '');
      this.button.classList.toggle('is-in-list', Boolean(item));
      if (this.label) {
        this.label.textContent = item
          ? `${this.dataset.labelIn ?? 'Í tilboðslista'} · ${group(item.qty)} stk`
          : this.dataset.labelAdd ?? 'Setja í tilboðslista';
      }
    };
  }

  /* --- <akk-quote-rail>: the boxes on the list, no prices (CEO 2026-10-13
     #19): thumbnail, name, a quantity field and a × to take a box off, with
     "Afturkalla" after. On a phone the rail is a tray that shows the box count
     instead (CSS). The CTA is always shown; its label changes with the list
     ("Fá tilboð án lista" when empty). Rows are reconciled by id so a focused
     quantity field survives a re-render. */
  class AkkQuoteRail extends HTMLElement {
    connectedCallback() {
      this.emptyEl = this.querySelector('[data-akk-empty]');
      this.filledEl = this.querySelector('[data-akk-filled]');
      this.boxesEl = this.querySelector('[data-akk-boxes]');
      this.itemsEl = this.querySelector('[data-akk-items]');
      this.itemsEl?.addEventListener('scroll', this.#markMore, { passive: true });
      this.undoEl = this.querySelector('[data-akk-undo]');
      this.cta = this.querySelector('[data-akk-cta]');
      this.dataset.akkReady = '';
      this.addEventListener('click', this.#onClick);
      this.addEventListener('change', this.#onChange);
      this.addEventListener('keydown', this.#onKeydown);
      document.addEventListener(CHANGE, this.#render);
      this.#render();
    }
    disconnectedCallback() {
      this.removeEventListener('click', this.#onClick);
      this.removeEventListener('change', this.#onChange);
      this.removeEventListener('keydown', this.#onKeydown);
      document.removeEventListener(CHANGE, this.#render);
    }
    #onClick = (event) => {
      if (event.target.closest('[data-akk-undo] button')) {
        const id = Quote.restore();
        const row = id ? this.itemsEl?.querySelector(`[data-id="${CSS.escape(id)}"]`) : null;
        row?.querySelector('[data-akk-remove]')?.focus();
        return;
      }
      const remove = event.target.closest('[data-akk-remove]');
      if (!remove) return;
      Quote.remove(remove.dataset.akkRemove);
      this.undoEl?.querySelector('button')?.focus();
    };
    /* A blank, zero or junk entry puts the old number back: removing is the
       × button's job, never a side effect of typing. */
    #commit(input) {
      const item = Quote.find(input.dataset.akkQty);
      if (!item) return;
      const qty = Number.parseInt(input.value, 10);
      if (!Number.isFinite(qty) || qty < 1) { input.value = String(item.qty); return; }
      if (qty !== item.qty) Quote.setQty(item.id, qty);
    }
    #onChange = (event) => {
      const input = event.target.closest('[data-akk-qty]');
      if (input) this.#commit(input);
    };
    #onKeydown = (event) => {
      if (event.key !== 'Enter') return;
      const input = event.target.closest('[data-akk-qty]');
      if (!input) return;
      event.preventDefault();
      this.#commit(input);
    };
    #render = () => {
      const { lines } = Quote.totals();
      const empty = lines === 0;
      this.emptyEl?.toggleAttribute('hidden', !empty);
      this.filledEl?.toggleAttribute('hidden', empty);
      this.itemsEl?.toggleAttribute('hidden', empty);
      if (this.boxesEl) this.boxesEl.textContent = boxes(lines);
      this.#renderItems();
      renderUndo(this.undoEl);
      if (this.cta) {
        const label = empty ? this.dataset.ctaEmpty : this.dataset.ctaFilled;
        if (label) this.cta.textContent = label;
      }
    };
    #renderItems() {
      const list = this.itemsEl;
      if (!list) return;
      const rows = new Map([...list.children].map((row) => [row.dataset.id, row]));
      const wanted = new Set(Quote.items.map((item) => item.id));
      for (const [id, row] of rows) if (!wanted.has(id)) row.remove();
      Quote.items.forEach((item, index) => {
        const row = rows.get(item.id) ?? this.#row(item);
        const input = row.querySelector('[data-akk-qty]');
        if (input && document.activeElement !== input) input.value = String(item.qty);
        const at = list.children[index] ?? null;
        if (at !== row) list.insertBefore(row, at);
      });
      this.#markMore();
    }
    /* Fade the bottom edge while boxes are hidden below the fold. */
    #markMore = () => {
      const list = this.itemsEl;
      if (!list) return;
      const more = list.scrollHeight - list.scrollTop - list.clientHeight > 4;
      list.toggleAttribute('data-akk-more', more);
    };
    #row(item) {
      const row = document.createElement('li');
      row.className = 'akk-qrail__item';
      row.dataset.id = item.id;

      const img = document.createElement('img');
      img.className = 'akk-qrail__img';
      img.alt = '';
      img.width = 40;
      img.height = 40;
      img.loading = 'eager';
      if (item.image) img.src = item.image;

      const title = document.createElement('span');
      title.className = 'akk-qrail__title';
      title.textContent = item.title;

      const remove = document.createElement('button');
      remove.type = 'button';
      remove.className = 'akk-qrail__remove';
      remove.dataset.akkRemove = item.id;
      remove.setAttribute('aria-label', `Fjarlægja ${item.title}`);
      remove.innerHTML = '<svg width="14" height="14" viewBox="0 0 14 14" aria-hidden="true" focusable="false"><path d="M2 2l10 10M12 2L2 12" stroke="currentColor" stroke-width="1.5" fill="none"/></svg>';

      row.append(img, title, remove);
      return row;
    }
  }

  /* --- <akk-quote-list>: the editable list on the tilboðslisti screen.
     Rows are reconciled by id, never rebuilt: rebuilding destroyed the input
     that had just fired change on blur, and would rip focus out of a field. */
  class AkkQuoteList extends HTMLElement {
    connectedCallback() {
      this.list = this.querySelector('[data-akk-lines]');
      this.emptyEl = this.querySelector('[data-akk-empty]');
      this.unitsEl = this.querySelector('[data-akk-units]');
      this.totalExEl = this.querySelector('[data-akk-total-ex]');
      this.totalIncEl = this.querySelector('[data-akk-total-inc]');
      this.fromNote = this.querySelector('[data-akk-from-note]');
      this.field = this.querySelector('[data-akk-field]');
      this.undoEl = this.querySelector('[data-akk-undo]');
      this.form = this.closest('form');
      this.addEventListener('click', this.#onClick);
      this.addEventListener('change', this.#onChange);
      this.addEventListener('keydown', this.#onKeydown);
      this.form?.addEventListener('submit', this.#onSubmit);
      document.addEventListener(CHANGE, this.#render);
      this.#render();
    }
    disconnectedCallback() {
      this.removeEventListener('click', this.#onClick);
      this.removeEventListener('change', this.#onChange);
      this.removeEventListener('keydown', this.#onKeydown);
      this.form?.removeEventListener('submit', this.#onSubmit);
      document.removeEventListener(CHANGE, this.#render);
    }
    #onClick = (event) => {
      if (event.target.closest('[data-akk-undo] button')) {
        event.preventDefault();
        const id = Quote.restore();
        const row = id ? this.list?.querySelector(`[data-id="${CSS.escape(id)}"]`) : null;
        row?.querySelector('[data-akk-remove]')?.focus();
        return;
      }
      const remove = event.target.closest('[data-akk-remove]');
      const dec = event.target.closest('[data-akk-dec]');
      const inc = event.target.closest('[data-akk-inc]');
      const control = remove ?? dec ?? inc;
      if (!control) return;
      event.preventDefault();
      const id = control.dataset.akkRemove ?? control.dataset.akkDec ?? control.dataset.akkInc;
      const item = Quote.find(id);
      if (!item) return;
      if (remove) {
        Quote.remove(id);
        this.undoEl?.querySelector('button')?.focus();
      } else if (dec) Quote.setQty(id, item.qty - 1);
      else Quote.setQty(id, item.qty + 1);
    };
    #onChange = (event) => {
      const input = event.target.closest('[data-akk-qty]');
      if (!input) return;
      const qty = Number.parseInt(input.value, 10);
      Quote.setQty(input.dataset.akkQty, Number.isFinite(qty) ? qty : 1);
    };
    #onKeydown = (event) => {
      if (event.key !== 'Enter') return;
      const input = event.target.closest('[data-akk-qty]');
      if (!input) return;
      event.preventDefault();
      const qty = Number.parseInt(input.value, 10);
      Quote.setQty(input.dataset.akkQty, Number.isFinite(qty) ? qty : 1);
    };
    #onSubmit = () => {
      if (this.field) this.field.value = this.#serialise();
    };
    #serialise() {
      if (Quote.items.length === 0) return '(Enginn tilboðslisti fylgdi)';
      const { lines, units, inc, exVat, varies } = Quote.totals();
      const rows = Quote.items.map((item) => {
        const unitEx = Quote.ex(item.price, item);
        const pre = item.varies ? 'frá ' : '';
        const line = `${group(item.qty)} × ${item.title}: ${pre}${money(unitEx)} án vsk. hvert, samtals ${pre}${money(unitEx * item.qty)} án vsk.`;
        return item.url ? `${line}\n   ${new URL(item.url, window.location.origin).href}` : line;
      });
      const note = varies ? '\nBox merkt „frá“ eru með breytilegu verði.' : '';
      return `${boxes(lines)} · ${gifts(units)}\n\n${rows.join('\n')}\n\nSamtals án vsk.: ${money(exVat)}\nSamtals með vsk.: ${money(inc)}${note}`;
    }
    #render = () => {
      const { lines, units, inc, exVat, varies } = Quote.totals();
      this.emptyEl?.toggleAttribute('hidden', lines > 0);
      if (this.list) {
        const rows = new Map([...this.list.children].map((row) => [row.dataset.id, row]));
        const wanted = new Set(Quote.items.map((item) => item.id));
        let focusTarget = null;
        for (const [id, row] of rows) {
          if (wanted.has(id)) continue;
          if (row.contains(document.activeElement)) {
            const neighbour = row.nextElementSibling ?? row.previousElementSibling;
            focusTarget = neighbour?.querySelector('[data-akk-remove]') ?? this.emptyEl?.querySelector('a');
          }
          row.remove();
        }
        if (focusTarget) focusTarget.focus();
        Quote.items.forEach((item, index) => {
          const row = rows.get(item.id) ?? this.#row(item);
          this.#update(row, item);
          const at = this.list.children[index] ?? null;
          if (at !== row) this.list.insertBefore(row, at);
        });
      }
      renderUndo(this.undoEl);
      if (this.unitsEl) this.unitsEl.textContent = gifts(units);
      if (this.totalExEl) this.totalExEl.textContent = money(exVat);
      if (this.totalIncEl) this.totalIncEl.textContent = money(inc);
      this.fromNote?.toggleAttribute('hidden', !varies);
      if (this.field) this.field.value = this.#serialise();
    };
    #row(item) {
      const row = document.createElement('li');
      row.className = 'akk-qlist__row';
      row.dataset.id = item.id;

      const img = document.createElement('img');
      img.className = 'akk-qlist__img';
      img.alt = '';
      img.width = 64;
      img.height = 64;
      if (item.image) img.src = item.image;

      const body = document.createElement('div');
      body.className = 'akk-qlist__body';
      const title = document.createElement('div');
      title.className = 'akk-qlist__title';
      if (item.url) {
        const link = document.createElement('a');
        link.className = 'akk-qlist__link';
        link.href = item.url;
        link.textContent = item.title;
        title.append(link);
      } else {
        title.textContent = item.title;
      }
      const unit = document.createElement('div');
      unit.className = 'akk-qlist__unit';
      unit.dataset.akkUnit = '';
      body.append(title, unit);

      const qty = document.createElement('div');
      qty.className = 'akk-qlist__qty';
      const dec = this.#stepButton('−', `Fækka — ${item.title}`);
      dec.dataset.akkDec = item.id;
      const label = document.createElement('label');
      label.className = 'visually-hidden';
      label.htmlFor = `akk-qty-${item.id}`;
      label.textContent = `Fjöldi — ${item.title}`;
      const input = document.createElement('input');
      input.className = 'akk-input akk-qlist__input';
      input.id = `akk-qty-${item.id}`;
      input.type = 'number';
      input.min = '1';
      input.step = '1';
      input.inputMode = 'numeric';
      input.dataset.akkQty = item.id;
      const inc = this.#stepButton('+', `Fjölga — ${item.title}`);
      inc.dataset.akkInc = item.id;
      qty.append(dec, label, input, inc);

      const line = document.createElement('div');
      line.className = 'akk-qlist__line';
      line.dataset.akkLine = '';

      const remove = document.createElement('button');
      remove.type = 'button';
      remove.className = 'akk-qlist__remove';
      remove.dataset.akkRemove = item.id;
      remove.textContent = '×';
      remove.setAttribute('aria-label', `Fjarlægja ${item.title} af tilboðslistanum`);

      row.append(img, body, qty, line, remove);
      return row;
    }
    #stepButton(glyph, label) {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'akk-qlist__step';
      button.textContent = glyph;
      button.setAttribute('aria-label', label);
      return button;
    }
    /* Values that change. The input is left alone while it has focus so a
       typed "25" is not overwritten by the "2" the first keystroke produced. */
    #update(row, item) {
      const pre = item.varies ? 'frá ' : '';
      const unit = row.querySelector('[data-akk-unit]');
      const line = row.querySelector('[data-akk-line]');
      const input = row.querySelector('[data-akk-qty]');
      if (unit) unit.textContent = `${priceLabel()} ${pre}${money(unitCents(item))}`;
      if (line) line.textContent = `${pre}${money(unitCents(item) * item.qty)}`;
      if (input && document.activeElement !== input) input.value = String(item.qty);
    }
  }

  /* --- <akk-quote-filters>: chips and sort over the cards in the DOM. A
     category chip and a price band combine with AND. ?tag= and ?min=&max= in
     the URL press the matching chips on arrival. Uppselt stays last. */
  class AkkQuoteFilters extends HTMLElement {
    connectedCallback() {
      this.grid = document.getElementById(this.dataset.grid ?? '');
      this.chips = [...this.querySelectorAll('[data-filter-group]')];
      this.sort = this.querySelector('[data-akk-sortby]');
      this.count = document.querySelector(this.dataset.count ?? '');
      this.emptyEl = document.querySelector(this.dataset.empty ?? '');
      if (!this.grid) return;
      this.cards = [...this.grid.children];
      this.order = new Map(this.cards.map((card, index) => [card, index]));
      this.addEventListener('click', this.#onClick);
      this.sort?.addEventListener('change', this.#apply);
      document.addEventListener(VSK_CHANGE, this.#apply);
      this.#pressFromUrl();
      this.#apply();
    }
    disconnectedCallback() {
      this.removeEventListener('click', this.#onClick);
      this.sort?.removeEventListener('change', this.#apply);
      document.removeEventListener(VSK_CHANGE, this.#apply);
    }
    #press(chip) {
      for (const other of this.chips) {
        if (other.dataset.filterGroup !== chip.dataset.filterGroup) continue;
        other.setAttribute('aria-pressed', String(other === chip));
      }
    }
    #pressFromUrl() {
      const params = new URLSearchParams(window.location.search);
      const tag = params.get('tag');
      const norm = (v) => (v && v !== '0' ? v : '');
      const min = norm(params.get('min'));
      const max = norm(params.get('max'));
      if (tag) {
        const chip = this.chips.find((c) => c.dataset.filterGroup === 'cat' && c.dataset.filterTag === tag);
        if (chip) this.#press(chip);
      }
      if (min || max) {
        const chip = this.chips.find((c) =>
          c.dataset.filterGroup === 'price' && norm(c.dataset.filterMin) === min && norm(c.dataset.filterMax) === max);
        if (chip) this.#press(chip);
      }
    }
    #onClick = (event) => {
      const chip = event.target.closest('[data-filter-group]');
      if (!chip) return;
      event.preventDefault();
      this.#press(chip);
      this.#apply();
    };
    #active(group) {
      return this.chips.find((chip) => chip.dataset.filterGroup === group && chip.getAttribute('aria-pressed') === 'true');
    }
    #apply = () => {
      const tag = this.#active('cat')?.dataset.filterTag ?? '';
      const band = this.#active('price');
      const min = Number.parseInt(band?.dataset.filterMin ?? '', 10);
      const max = Number.parseInt(band?.dataset.filterMax ?? '', 10);
      let shown = 0;
      for (const card of this.cards) {
        const tags = (card.dataset.tags ?? '').split('|');
        /* A band is read against the price on show, án or með vsk. */
        const cents = Number.parseInt(card.dataset.price ?? '0', 10);
        const ref = { id: card.dataset.productId, handle: card.dataset.handle };
        const price = (mode() === 'inc' ? Quote.inc(cents, ref) : Quote.ex(cents, ref)) / 100;
        const visible = (tag === '' || tags.includes(tag)) &&
          (!Number.isFinite(min) || price >= min) && (!Number.isFinite(max) || price < max);
        card.toggleAttribute('hidden', !visible);
        if (visible) shown += 1;
      }
      this.#sort();
      if (this.count) this.count.textContent = items(shown);
      this.emptyEl?.toggleAttribute('hidden', shown > 0);
    };
    #sort() {
      const modeValue = this.sort?.value ?? 'manual';
      const sunk = (card) => (card.dataset.available === 'false' ? 1 : 0);
      const price = (card) => Quote.inc(Number.parseInt(card.dataset.price ?? '0', 10), { handle: card.dataset.handle });
      const sorted = [...this.cards].sort((a, b) => {
        const byStock = sunk(a) - sunk(b);
        if (byStock !== 0) return byStock;
        if (modeValue === 'price-asc') return price(a) - price(b);
        if (modeValue === 'price-desc') return price(b) - price(a);
        if (modeValue === 'title-asc') return (a.dataset.title ?? '').localeCompare(b.dataset.title ?? '', 'is');
        return this.order.get(a) - this.order.get(b);
      });
      for (const card of sorted) this.grid.append(card);
    }
  }

  readVatDivisor();
  Quote.load();

  /* The list page renders this marker on a successful send. Emptying here,
     not on submit, keeps the list intact if the POST fails. */
  if (document.querySelector('[data-akk-quote-sent]')) {
    Quote.items = [];
    writeJSON(STORE_KEY, []);
  }
  if (readJSON(VSK_KEY, 'ex') === 'inc') document.documentElement.dataset.akkVsk = 'inc';

  customElements.define('akk-vsk-toggle', AkkVskToggle);
  customElements.define('akk-quote-add', AkkQuoteAdd);
  customElements.define('akk-quote-rail', AkkQuoteRail);
  customElements.define('akk-quote-list', AkkQuoteList);
  customElements.define('akk-quote-filters', AkkQuoteFilters);

  window.addEventListener('storage', (event) => {
    if (event.key !== STORE_KEY) return;
    Quote.load();
    document.dispatchEvent(new CustomEvent(CHANGE, { detail: Quote.totals() }));
  });
})();
